/**
 * The single animation clock. Every animated value in the app is a pure
 * function of `now` (performance.now()), so MapLibre's render loop and ours
 * cannot drift apart. Owned by the main loop.
 *
 *   const off = ticker.add((now, dt) => { ...; return keepGoing; });
 *
 * - One rAF loop for the whole app; stops when nothing is subscribed.
 * - Pauses while the document is hidden (subscribers get a large dt on resume,
 *   so compute from absolute time, not accumulated dt).
 * - `ticker.setIdleFps(30)` halves the rate when only "idle" subscribers
 *   (ambient pulses) are active.
 */
export type TickFn = (now: number, dt: number) => boolean | void;

interface Sub { fn: TickFn; idle: boolean }

class Ticker {
  private subs = new Set<Sub>();
  private raf = 0;
  private last = 0;
  private idleFps = 30;
  private skip = false;
  private hidden = typeof document !== "undefined" && document.visibilityState === "hidden";

  constructor() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        this.hidden = document.visibilityState === "hidden";
        if (!this.hidden) this.kick();
      });
    }
  }

  /** Subscribe. Return false from fn to unsubscribe automatically.
   *  idle=true marks ambient work that may run at the reduced idle rate. */
  add(fn: TickFn, o: { idle?: boolean } = {}): () => void {
    const sub: Sub = { fn, idle: !!o.idle };
    this.subs.add(sub);
    this.kick();
    return () => {
      this.subs.delete(sub);
    };
  }

  setIdleFps(fps: number) {
    this.idleFps = fps;
  }

  get size() {
    return this.subs.size;
  }

  private kick() {
    if (!this.raf && this.subs.size && !this.hidden && typeof requestAnimationFrame !== "undefined") {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.frame);
    }
  }

  private frame = (now: number) => {
    this.raf = 0;
    if (this.hidden) return;
    const dt = now - this.last;
    const onlyIdle = [...this.subs].every((s) => s.idle);
    // At the idle rate, run every other frame (≈30 fps on 60 Hz).
    this.skip = onlyIdle && this.idleFps < 60 ? !this.skip : false;
    if (!this.skip) {
      this.last = now;
      for (const s of [...this.subs]) {
        let keep: boolean | void;
        try {
          keep = s.fn(now, dt);
        } catch (err) {
          console.error("[ticker]", err);
          keep = false;
        }
        if (keep === false) this.subs.delete(s);
      }
    }
    if (this.subs.size) this.raf = requestAnimationFrame(this.frame);
  };
}

export const ticker = new Ticker();

/** Promise that resolves after `ms`, rejecting with AbortError if signal aborts. */
export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(abortError());
    const id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(id);
      reject(abortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

export const isAbort = (e: unknown): boolean => e instanceof DOMException && e.name === "AbortError";

/**
 * Tween a value over `duration` ms on the ticker. `ease` maps 0..1 → 0..1.
 * Resolves true when finished, false if cancelled by signal.
 */
export function tween(
  duration: number,
  ease: (t: number) => number,
  onUpdate: (e: number, t: number) => void,
  o: { signal?: AbortSignal; delay?: number; idle?: boolean } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    if (o.signal?.aborted) return resolve(false);
    const start = performance.now() + (o.delay ?? 0);
    if (duration <= 0) {
      onUpdate(1, 1);
      return resolve(true);
    }
    const off = ticker.add(
      (now) => {
        if (o.signal?.aborted) {
          resolve(false);
          return false;
        }
        if (now < start) return true;
        const t = Math.min(1, (now - start) / duration);
        onUpdate(ease(t), t);
        if (t >= 1) {
          resolve(true);
          return false;
        }
        return true;
      },
      { idle: o.idle },
    );
    o.signal?.addEventListener("abort", () => {
      off();
      resolve(false);
    }, { once: true });
  });
}
